import React from 'react';
import useReveal from '../../hooks/useReveal';
import { BAR_ANIMATION_MS, REVEAL_DELAY_MS, REVEAL_DURATION_MS, toMs } from '../../constants/animation';

interface EmploymentStatusRendererProps {
  title: string;
  data: any;
  primaryColor?: string;
}

export default function EmploymentStatusRenderer({ title, data, primaryColor = '#14B8A6' }: EmploymentStatusRendererProps) {
  if (!data) return null;
  const { ref, isVisible } = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });

  // Handle both array and object formats - look for the primary employment status
  let primaryStatus = null;
  let index = 0;
  let composition = 0;

  if (Array.isArray(data)) {
    // Find the first item with meaningful data
    primaryStatus = data.find(item => item && (item.value > 0 || item.index > 0 || item.composition > 0));
    if (primaryStatus) {
      index = primaryStatus.index || primaryStatus.value || 0;
      composition = primaryStatus.composition || 0;
    }
  } else if (data && typeof data === 'object') {
    // Take the first entry with meaningful data
    const entries = Object.entries(data);
    const meaningfulEntry = entries.find(([, value]) => typeof value === 'number' && value > 0);
    if (meaningfulEntry) {
      primaryStatus = { label: meaningfulEntry[0], value: meaningfulEntry[1] };
      index = meaningfulEntry[1] as number;
      composition = 46; // Default composition percentage as shown in reference
    }
  }

  // If we found a single status item, extract its details
  if (primaryStatus) {
    const statusName = primaryStatus.label || primaryStatus.name || 'FULL-TIME';
    const statusIndex = index;
    const statusComposition = composition;

    return (
      <div
        ref={ref}
        className="bg-white rounded-lg shadow-md p-6"
        style={{
          transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
          transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
          opacity: isVisible ? 1 : 0.25,
          transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
        }}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <div className="text-xs text-gray-500 mb-6">BY INDEX with minimum composition: 10%</div>
        
        <div className="text-center mb-6">
          <div className="text-xl font-bold tracking-wider mb-4" style={{ color: primaryColor }}>{statusName.toUpperCase()}</div>
          
          <div className="flex items-center justify-center gap-8 mb-6">
            <div className="rounded-lg px-4 py-3 text-center" style={{ backgroundColor: `${primaryColor}20` }}>
              <div className="text-xs text-gray-600 mb-1">INDEX</div>
              <div className="text-2xl font-bold text-gray-900">{statusIndex}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-600 mb-1">% COMPOSITION</div>
              <div className="text-4xl font-bold" style={{ color: primaryColor }}>{statusComposition}%</div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden">
            <div 
                className="h-full rounded-full flex items-center justify-end pr-3 text-white text-sm font-bold"
                style={{ 
                  width: `${isVisible ? Math.min(100, Math.max(10, statusIndex)) : 0}%`,
                  backgroundColor: primaryColor,
                  transition: `width ${toMs(BAR_ANIMATION_MS)} ease-out`,
                  transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
                }}
            >
              {statusIndex}
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="text-xs text-gray-400">100</div>
          <div className="text-xs text-gray-400">Online Adult Population</div>
        </div>
      </div>
    );
  }

  return null;
}
